import { registerRemotes, loadRemote } from "@module-federation/runtime";

/**
 * The one way this app reaches the Audience Layer.
 *
 * The remote used to be declared in the federation build config, which meant
 * the runtime initialised it while the app was still starting: it fetched the
 * manifest before the entry finished evaluating, so the page could not paint
 * until that request settled. Measured by throttling only the manifest to 3G,
 * which delayed first paint by exactly the 2.06s the fetch took, on the home
 * page, which renders no part of the layer at all.
 *
 * So nothing about the remote exists until one of its consumers renders. Each
 * of them wraps this in React.lazy, so a slow remote suspends that component
 * alone and a failed one throws into its own error boundary.
 */

const REMOTE_NAME = "audienceLayer";

/**
 * Where the layer is deployed, per environment.
 *
 * Read here rather than in vite.config so the URL travels with the code that
 * uses it, and so nothing in the build graph has to know the remote exists.
 */
const REMOTE_ENTRY =
  import.meta.env.VITE_AUDIENCE_LAYER_URL || "https://audience.htp.com/mf-manifest.json";

// Registration is idempotent in the federation runtime, but doing it once is
// clearer than relying on that, and this is called from several components.
let registered = false;

function registerOnce(): void {
  if (registered) return;
  registered = true;
  registerRemotes([{ name: REMOTE_NAME, entry: REMOTE_ENTRY, type: "module" }]);
}

/**
 * One exposed module from the layer, e.g. "App" or "StudioClips".
 *
 * Returns the module namespace, so a caller can hand it straight to
 * React.lazy, which wants `{ default: Component }`.
 *
 * Throws when the remote cannot be resolved at all. loadRemote answers null
 * there, and returning that to React.lazy would have it render `null` as a
 * component and fail somewhere less obvious than the boundary that is waiting
 * for exactly this.
 */
export async function loadAudienceLayerModule<T>(exposed: string): Promise<T> {
  registerOnce();
  const mod = await loadRemote<T>(`${REMOTE_NAME}/${exposed}`);
  if (!mod) throw new Error(`Audience Layer remote could not load ${exposed}`);
  return mod;
}
